import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { TenantProvider, useTenant } from '@/contexts/TenantContext';
import { LanguageProvider } from '@/i18n/LanguageContext';
import LandingPage from './pages/LandingPage';
import SectionListPage from './pages/SectionListPage';
import ProjectPage from './pages/ProjectPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { SAHome } from './components/superadmin/SAHome';
import { SAUsers } from './components/superadmin/SAUsers';
import { SAUserProfile } from './components/superadmin/SAUserProfile';
import { SAInvites } from './components/superadmin/SAInvites';
import { SAWaitlist } from './components/superadmin/SAWaitlist';
import { SAFinanceiro } from './components/superadmin/SAFinanceiro';
import { SAEmails } from './components/superadmin/SAEmails';
import InviteAcceptPage from './pages/InviteAcceptPage';
import ClientApprovalPage from './pages/ClientApprovalPage';
import AboutPage from './pages/AboutPage';
import UserHomePage from './pages/UserHomePage';
import FreePagePublic from './pages/FreePagePublic';
import MenuGalleryPublic from './pages/MenuGalleryPublic';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminResetPasswordPage from './pages/AdminResetPasswordPage';
import NotFound from './pages/NotFound';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SobrePage from './pages/SobrePage';
import ContatoPage from './pages/ContatoPage';
import TermosPage from './pages/TermosPage';
import PrivacidadePage from './pages/PrivacidadePage';
import EntrarPage from './pages/EntrarPage';
import OAuthConsentPage from './pages/OAuthConsentPage';
import CriarContaPage from './pages/CriarContaPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import OnboardingPage from './pages/OnboardingPage';
import { PageTransition } from './components/PageTransition';


/**
 * When accessed via subdomain (samuel.criaaa.com) or custom domain,
 * all routes are scoped to that tenant — no /:username prefix needed.
 * Uses relative paths so it can be mounted under both "/" and "/en".
 */
function TenantInnerRoutes() {
  return (
    <Routes>
      <Route index element={<UserHomePage />} />
      <Route path="admin" element={<AdminDashboard />} />
      <Route path="admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="login" element={<AdminLogin />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="aprovacao/:slug" element={<ClientApprovalPage />} />
      <Route path="galeria/:projectSlug" element={<ProjectPage />} />
      <Route path="p/:pageSlug" element={<FreePagePublic />} />
      <Route path="g/:gallerySlug" element={<MenuGalleryPublic />} />
      <Route path=":sectionSlug" element={<SectionListPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function TenantRoutes({ username }: { username: string }) {
  const location = useLocation();
  return (
    <PageTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/en/*" element={<TenantInnerRoutes />} />
        <Route path="/*" element={<TenantInnerRoutes />} />
      </Routes>
    </PageTransition>
  );
}

/**
 * Default routes for criaaa.com root domain and localhost development.
 * Relative paths so they can be mounted under both "/" and "/en".
 */
function DefaultInnerRoutes() {
  return (
    <Routes>
      <Route index element={<LandingPage />} />
      <Route path="sobre" element={<SobrePage />} />
      <Route path="contato" element={<ContatoPage />} />
      <Route path="termos" element={<TermosPage />} />
      <Route path="privacidade" element={<PrivacidadePage />} />
      <Route path="entrar" element={<EntrarPage />} />
      <Route path="criar-conta" element={<CriarContaPage />} />
      <Route path="auth/callback" element={<AuthCallbackPage />} />
      <Route path=".lovable/oauth/consent" element={<OAuthConsentPage />} />
      <Route path="onboarding" element={<OnboardingPage />} />
      <Route path="admin" element={<SuperAdminDashboard />}>
        <Route index element={<SAHome />} />
        <Route path="usuarios" element={<SAUsers />} />
        <Route path="usuarios/:id" element={<SAUserProfile />} />
        <Route path="convites" element={<SAInvites />} />
        <Route path="espera" element={<SAWaitlist />} />
        <Route path="financeiro" element={<SAFinanceiro />} />
        <Route path="emails" element={<SAEmails />} />
      </Route>
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="convite/:token" element={<InviteAcceptPage />} />
      <Route path="verificar-email" element={<VerifyEmailPage />} />
      <Route path="aprovacao/:slug" element={<ClientApprovalPage />} />
      <Route path=":username/about" element={<AboutPage />} />
      <Route path=":username/admin" element={<AdminDashboard />} />
      <Route path=":username/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path=":username/login" element={<AdminLogin />} />
      <Route path=":username/galeria/:projectSlug" element={<ProjectPage />} />
      <Route path=":username/p/:pageSlug" element={<FreePagePublic />} />
      <Route path=":username/g/:gallerySlug" element={<MenuGalleryPublic />} />
      <Route path=":username/:sectionSlug" element={<SectionListPage />} />
      <Route path=":username" element={<UserHomePage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function DefaultRoutes() {
  const location = useLocation();
  return (
    <PageTransition>
      <Routes location={location} key={location.pathname}>
        <Route path="/en/*" element={<DefaultInnerRoutes />} />
        <Route path="/*" element={<DefaultInnerRoutes />} />
      </Routes>
    </PageTransition>
  );
}

function AppRouter() {
  const { username, loading, notFound } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F6F2' }}>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, opacity: 0.4 }}>Carregando...</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F6F2' }}>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>Site não encontrado.</span>
      </div>
    );
  }

  if (username) {
    return <TenantRoutes username={username} />;
  }

  return <DefaultRoutes />;
}

const App = () => (
  <BrowserRouter>
    <TenantProvider>
      <LanguageProvider>
        <AppRouter />
      </LanguageProvider>
    </TenantProvider>
  </BrowserRouter>
);

export default App;
